import React, { Component } from 'react'
import propTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import { action, extendObservable } from 'mobx'
import classNames from 'classnames'
import { autocompleteMentions } from '@atheneum/api'

// Require froala files.
import 'froala-editor/js/froala_editor.pkgd.min.js'
import 'froala-editor/css/froala_style.min.css'
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/js/plugins/colors.min.js'
import 'froala-editor/js/plugins/font_size.min.js'
import 'font-awesome/css/font-awesome.css'

import FroalaEditor from 'react-froala-wysiwyg'
import * as Tribute from 'tributejs'

const colors = [
  '#33343A',
  '#D7DCE4',
  '#858485',
  '#BCC3CF',
  '#F0F0F0',
  '#E8E8E8',
  '#7E8DA2',
  '#B6C0CD',
  '#F1F1F8',
  '#F9F9FC',
  '#FFFFFF',
  '#FAFAFC',
  '#FFF7ED',
  '#202A41',
  '#2A4261',
  '#323D54',
  '#3A86BF',
  '#429ADC',
  '#67B0E7',
  '#DDEDF9',
  '#99C8EC',
  '#39AC57',
  '#67D158',
  '#FEF8F0',
  '#FCE5C8',
  '#EEB355',
  '#FF6D2A',
  '#FFB049',
  '#FD8249',
  '#FF4F4F',
  '#FF5858',
  '#8A2619'
]

const fontSize = [
  '8',
  '9',
  '10',
  '11',
  '12',
  '14',
  '18',
  '24',
  '30',
  '36',
  '48',
  '60',
  '72',
  '96'
]

const toolbarButtons = [
  'paragraphFormat',
  '|',
  'formatOL',
  'formatUL',
  '|',
  'insertLink',
  '|',
  'clearFormatting',
  'underline',
  'italic',
  'bold'
]

class RichTextEditor extends Component {
  static propTypes = {
    label: propTypes.oneOfType([propTypes.string, propTypes.element]),
    placeholder: propTypes.string,
    value: propTypes.string,
    readOnly: propTypes.bool,
    toolbarInline: propTypes.bool,
    isExtractEplsDraft: propTypes.bool,
    error: propTypes.string,
    onFocus: propTypes.func,
    onBlur: propTypes.func,
    onChange: propTypes.func,
    skipWrapper: propTypes.bool,
    mentions: propTypes.bool,
    large: propTypes.bool,
    replacements: propTypes.object,
    insertLinks: propTypes.bool,
    searchStore: propTypes.object,
    'data-cy': propTypes.string
  }

  constructor(props) {
    super(props)

    extendObservable(this, {
      config: {
        key: process.env.REACT_APP_FROALA_KEY,
        enter: window.$.FroalaEditor.ENTER_DIV,
        quickInsertTags: [],
        charCounterCount: false,
        toolbarInline: props.toolbarInline,
        placeholderText: props.placeholder,
        linkInsertButtons: [],
        pastePlain: true,
        toolbarButtons: props.isExtractEplsDraft
          ? ['color', '|', 'undo', 'redo', '|', ...toolbarButtons, 'fontSize']
          : toolbarButtons,
        htmlAllowedStyleProps: [
          'text-align',
          'font-size',
          'width',
          'height',
          'vertical-align',
          'font-weight',
          'font-style',
          'text-decoration',
          'display',
          'direction',
          'letter-spacing',
          'word-spacing',
          'color',
          'background',
          'border',
          'margin'
        ],
        colorsText: colors,
        colorsBackground: colors,
        fontSize,
        events: {
          'froalaEditor.focus': this.onFocus,
          'froalaEditor.blur': this.onBlur,
          'froalaEditor.initialized': this.initTribute
        },
        linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
        ...props.config
      },
      isFocused: false,
      isToolboxClosing: false,
      isToolboxClosed: true
    })
  }

  initTribute = (e, editor) => {
    if (this.props.mentions) {
      this.initMentions(e, editor)
    }
    if (this.props.insertLinks) {
      this.initLinkInsert(e, editor)
    }
    if (this.props.replacements) {
      this.initReplacements(e, editor)
    }
    if (this.props.readOnly) {
      editor.edit.off()
    }
  }

  initReplacements = (e, editor) => {
    const multiTribute = new Tribute({
      trigger: '#',
      allowSpaces: false,
      values: (text, cb) => {
        // replacements should be object at this point
        const { replacements } = this.props

        if (replacements) {
          const replacementArray = Object.keys(this.props.replacements).map(
            key => {
              return {
                key,
                value: replacements[key]
              }
            }
          )
          cb(replacementArray.filter(r => r.key.includes(text)))
        }
      },
      selectTemplate: function (item) {
        return `<span title="${item.original.key}" contenteditable="false" style="color: blue" class="fr-deletable fr-tribute" data-replacement="${item.original.key}">${item.original.value}</span>`
      }
    })

    multiTribute.attach(editor.el)

    editor.events.on(
      'keydown',
      function (e) {
        if (
          e.which === window.$.FroalaEditor.KEYCODE.ENTER &&
          multiTribute.isActive
        ) {
          return false
        }
      },
      true
    )
  }

  initLinkInsert = (e, editor) => {
    const multiTribute = new Tribute({
      trigger: '#',
      allowSpaces: true,
      values: (text, cb) => {
        this.props.searchStore.searchExtern(text).then(res => {
          cb(
            res.map(r => ({
              key: `${r.menuTitle} (${r.itemType})`,
              type: r.itemType,
              name: r.menuTitle,
              value: r.link
            }))
          )
        })
      },
      selectTemplate: function (item) {
        return `<a class="fr-deletable fr-tribute" href=${item.original.value}>${item.original.name}</a>`
      }
    })

    multiTribute.attach(editor.el)

    editor.events.on(
      'keydown',
      function (e) {
        if (
          e.which === window.$.FroalaEditor.KEYCODE.ENTER &&
          multiTribute.isActive
        ) {
          return false
        }
      },
      true
    )
  }

  initMentions = (e, editor) => {
    const mentionTribute = new Tribute({
      trigger: '@',
      values: function (text, cb) {
        autocompleteMentions(text).then(res => {
          cb(
            res.map(r => ({
              key: r.name,
              type: r.type,
              value: r.id
            }))
          )
        })
      },
      menuItemTemplate: item => `${item.original.key} (${item.original.type})`,
      selectTemplate: function (item) {
        return `<span class="fr-deletable fr-tribute mention"
        ${
          item.original.type === 'employee'
            ? 'notify-employee-id'
            : 'notify-team-id'
        }="${item.original.value}"
        >@${item.original.key}
      </span>`
      }
    })

    mentionTribute.attach(editor.el)

    editor.events.on(
      'keydown',
      function (e) {
        if (
          e.which === window.$.FroalaEditor.KEYCODE.ENTER &&
          mentionTribute.isActive
        ) {
          return false
        }
      },
      true
    )
  }

  onFocus = action((e, editor) => {
    this.isFocused = true
    this.isToolboxClosed = false
  })

  onBlur = action((e, editor) => {
    this.isFocused = false
    this.closeToolbox()
  })

  closeToolbox = action(() => {
    this.isToolboxClosing = true
    setTimeout(() => {
      this.isToolboxClosed = true
      this.isToolboxClosing = false
    }, 300) // animation speed
  })

  render() {
    const { label, toolbarRight, onChange, skipWrapper, ...props } = this.props
    const text = this.props.value

    const error = this.props.error
    const groupClasses = classNames({
      'expert-form__input-group': true,
      'expert-form__input-group--error': error
    })

    const isToolboxActive = !this.isToolboxClosed && !this.isToolboxClosing
    const editorClasses = classNames({
      'left-text': true,
      'rich-text-wrapper': true,
      'rich-text-wrapper--large': props.large,
      'rich-text-wrapper--toolbar-right': toolbarRight,
      'rich-text-wrapper--focused': this.isFocused,
      'rich-text-wrapper--toolboxActive': isToolboxActive,
      'rich-text-wrapper--toolboxClosing': this.isToolboxClosing,
      'rich-text-wrapper--error': error
    })

    const editor = (
      <div className={editorClasses} data-cy={this.props['data-cy']}>
        <FroalaEditor
          tag="textarea"
          height={'calc(100vh - 100px)'}
          model={text}
          onModelChange={value => {
            this.props.onChange(value)
          }}
          {...props}
          config={this.config}
        />
        {error}
      </div>
    )

    if (skipWrapper) return editor

    return (
      <div className={groupClasses}>
        <label className="expert-form__label">{label}</label>
        <div className="expert-form__input-wrapper">{editor}</div>
      </div>
    )
  }
}

export default inject('searchStore')(observer(RichTextEditor))
